/**
 * Frame - Main frame component
 * 
 * CHANGE HISTORY:
 * - 251025_1651 [pending] danielbeer: Initial creation
 * 
 * The top-level wrapper around all OpCentral apps.
 * Provides white-labeling, theming, and context.
 */

import React from 'react';
import { FramrProvider } from '../FramrContext.js';
import { FrameHeader } from './FrameHeader.js';
import { FrameFooter } from './FrameFooter.js';
import { FrameSidebar } from './FrameSidebar.js';
import type { FrameProps } from '../types.js';
import './Frame.css';

export function Frame({
  config,
  children,
  header,
  footer,
  sidebar,
  onConfigChange,
  onError,
  loading = false,
  loadingComponent,
}: FrameProps) {
  const showHeader = config?.showHeader ?? true;
  const showFooter = config?.showFooter ?? true;
  const showSidebar = config?.showSidebar ?? false;
  const layout = config?.layout || 'default';

  // Handle errors
  const handleError = (error: Error) => {
    console.error('[Framr] Error:', error);
    onError?.(error);
  };

  // Render loading state
  if (loading) {
    return (
      <div className="framr-loading">
        {loadingComponent || <div className="framr-spinner">Loading...</div>}
      </div>
    );
  }

  return (
    <FramrProvider initialConfig={config}>
      <div className={`framr-container framr-layout-${layout}`} data-theme={config?.theme || 'auto'}>
        {/* Header */}
        {showHeader && (
          <div className="framr-header">
            {header || (
              <FrameHeader
                logo={config?.orgLogo || config?.appLogo}
                appName={config?.appName}
              />
            )}
          </div>
        )}

        {/* Main content area */}
        <div className="framr-main">
          {/* Sidebar */}
          {showSidebar && (
            <div className="framr-sidebar">
              {sidebar || <FrameSidebar />}
            </div>
          )}

          {/* Content */}
          <div className="framr-content">
            <ErrorBoundary onError={handleError}>
              {children}
            </ErrorBoundary>
          </div>
        </div>

        {/* Footer */}
        {showFooter && (
          <div className="framr-footer">
            {footer || (
              <FrameFooter
                copyright={config?.orgName ? `© ${new Date().getFullYear()} ${config.orgName}` : undefined}
              />
            )}
          </div>
        )}
      </div>
    </FramrProvider>
  );
}

/**
 * Error Boundary Component
 */
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: (error: Error) => void },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Framr] Error caught by boundary:', error, errorInfo);
    this.props.onError?.(error);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="framr-error">
          <h2>Something went wrong</h2>
          <p>{this.state.error?.message}</p>
          <button onClick={() => this.setState({ hasError: false })}>
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
